import type { OperationsAttentionKey, OperationsItemsSortKey, OperationsPenKey, OperationsStartRiskKey } from "@/api/endpoints";
import { actorRoleLabel, ATTENTION_LABEL, PEN_LABEL, START_RISK_LABEL, workTypeLabel } from "./operationsLabels";

/** Sort, facet selections and search for the drill-through drawer (all applied by the API). */
export interface DrawerView {
  sort?: OperationsItemsSortKey;
  dir?: "asc" | "desc";
  attention: OperationsAttentionKey[];
  pendingWith: string[];
  /** Flow row labels (the Type column). */
  workType: string[];
  /** The Start date chips (2026-10-24). */
  startRisk: OperationsStartRiskKey[];
  /** The Owner, Team and Division dropdowns (2026-10-26); Manager (2026-10-27). */
  owner: string[];
  team: string[];
  division: string[];
  manager: string[];
  /** The Engagement type dropdown (2026-10-28). */
  engagement: string[];
  /** The "Who holds the pen" chips (2026-10-28). */
  holder: OperationsPenKey[];
  q: string;
}

export const SORT_LABEL: Record<OperationsItemsSortKey, string> = {
  worker: "worker",
  client: "client",
  type: "type",
  stage: "stage",
  age: "age",
  pendingFor: "pending for",
  attention: "attention",
  pendingWith: "pending with",
  owner: "owner",
  startLead: "start date",
};

/** Mirrors OperationsItemsView.DefaultDescending: numbers and severity start high-first. */
export function defaultDescending(sort: OperationsItemsSortKey): boolean {
  return sort === "age" || sort === "pendingFor" || sort === "attention" || sort === "startLead";
}

/** The sort the drawer is actually showing (the API's default is pending for, desc). */
export function effectiveSort(view: Pick<DrawerView, "sort" | "dir">): { sort: OperationsItemsSortKey; desc: boolean } {
  if (!view.sort) return { sort: "pendingFor", desc: true };
  return { sort: view.sort, desc: view.dir ? view.dir === "desc" : defaultDescending(view.sort) };
}

/** Clicking a header: the same column flips direction, another column starts in its natural one. */
export function nextSort(view: Pick<DrawerView, "sort" | "dir">, column: OperationsItemsSortKey): Pick<DrawerView, "sort" | "dir"> {
  const current = effectiveSort(view);
  if (current.sort === column) return { sort: column, dir: current.desc ? "asc" : "desc" };
  return { sort: column, dir: undefined };
}

export function sortSummary(view: Pick<DrawerView, "sort" | "dir">): string {
  const { sort, desc } = effectiveSort(view);
  return `sorted by ${SORT_LABEL[sort]} ${desc ? "↓" : "↑"}`;
}

/** Display name of a pending-with facet key ("ClientService" → "Client Service", "WorkOrder" → "Work order"). */
export function pendingWithLabel(key: string): string {
  if (key === "None") return "No pending action";
  if (key === "WorkOrder") return "Work order";
  return actorRoleLabel(key);
}

/** "180 of 370 items" while the drawer's filters narrow the bucket, else "370 items". */
export function countLabel(total: number, bucket: number): string {
  const noun = (n: number) => (n === 1 ? "item" : "items");
  return total < bucket ? `${total} of ${bucket} ${noun(bucket)}` : `${total} ${noun(total)}`;
}

export function hasViewFilters(view: DrawerView): boolean {
  return view.attention.length > 0 || view.pendingWith.length > 0 || view.workType.length > 0 || view.startRisk.length > 0 || view.owner.length > 0 || view.team.length > 0 || view.division.length > 0 || view.manager.length > 0 || view.engagement.length > 0 || view.holder.length > 0 || view.q.trim().length > 0;
}

/** "Blocked · Client Service · “deloitte”": what the drawer is filtered to, for the status line. */
export function viewFilterSummary(view: DrawerView): string {
  return [
    ...view.attention.map((a) => ATTENTION_LABEL[a]),
    ...view.holder.map((h) => PEN_LABEL[h]),
    ...view.pendingWith.map(pendingWithLabel),
    ...view.workType.map(workTypeLabel),
    ...view.engagement,
    ...view.startRisk.map((r) => START_RISK_LABEL[r]),
    ...view.division,
    ...view.manager,
    ...view.team,
    ...view.owner,
    ...(view.q.trim() ? [`“${view.q.trim()}”`] : []),
  ].join(" · ");
}
